import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import leasdrboardMemberListImg from "assets/leaderboards-member.png";
import { AccordionSection } from "components/FormControls/AcordionSection/AccordionSection";
import { FormSection } from "components/FormControls/FormSection";
import { HelpPanel } from "components/HelpPanel/HelpPanel";
import { ReactComponent as Lock03Icon } from "components/Icons/lock-03.svg";
import { InfoBox } from "components/Layout/InfoBox";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { useState } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";
import { KeyValuePair } from "utilities/objects";
import { useStyles } from "../styles";
import { CustomAttributesSection } from "./Sections/CustomAttributes";
import { DateSection } from "./Sections/DateSection";
import { NameSection } from "./Sections/NameSection";
import { Status } from "./Sections/Status";
import { TrackedMetricsSection } from "./Sections/TrackedMetricsSection";
import { VisibilitySection } from "./Sections/VisibilitySection";

export enum Sections {
  TITLE = "title",
  TIMEFRAME = "timeframe",
  VISIBILITY = "visibility",
  CUSTOM_ATTRIBUTES = "customAtributes",
  TRACKED_METRIC = "trackedMetric",
  STATUS = "status",
}

interface Props {
  translations: {
    description?: string;
    locale?: string;
    name?: string;
  }[];
  availableLanguages: DefaultLanguageDataState[];
  labels: KeyValuePair[];
  sectionErrors: {
    title: boolean;
    startEndDate: boolean;
    visibility: boolean;
    attributes: boolean;
  };
  isEditMode: boolean;
}

export function Info({
  translations,
  availableLanguages,
  labels,
  sectionErrors,
  isEditMode,
}: Props) {
  const { message } = useMessage();
  const classes = useStyles();
  const [activeSection, setActiveSection] = useState<Sections | false>(
    Sections.TITLE,
  );

  return (
    <Grid container spacing={12}>
      <Grid size={{ xs: 8 }}>
        <FormSection>
          {isEditMode && (
            <InfoBox
              customIcon={<Lock03Icon />}
              text={message({ id: "trackedMetricsEditDesc" })}
              type="warning"
              className={classes.warningText}
            />
          )}
          <div>
            <Typography className={classes.sectionTitle}>
              {message({ id: "basicInfo" })}
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              {message({ id: "leaderboardInfoDescription" })}
            </Typography>
          </div>
          <AccordionSection
            idx={1}
            sectionId={Sections.TITLE}
            title="titleAndDescription"
            details={
              <>
                <NameSection
                  translations={translations}
                  availableLanguages={availableLanguages}
                />
                <OLButton
                  className={classes.sectionNextBtn}
                  variant="contained"
                  onClick={() => setActiveSection(Sections.TIMEFRAME)}
                >
                  {`${message({ id: "next" })}: ${message({ id: "timeframe" })}`}
                </OLButton>
              </>
            }
            isEditMode={false}
            isExpanded={activeSection === Sections.TITLE}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
            hasError={sectionErrors.title}
          />
          <AccordionSection
            idx={2}
            sectionId={Sections.TIMEFRAME}
            title="timeframe"
            subtitle="setLeaderboardTimeframe"
            details={
              <>
                <DateSection />
                <OLButton
                  className={classes.sectionNextBtn}
                  variant="contained"
                  onClick={() => setActiveSection(Sections.VISIBILITY)}
                >
                  {`${message({ id: "next" })}: ${message({ id: "visibility" })}`}
                </OLButton>
              </>
            }
            isEditMode={false}
            isExpanded={activeSection === Sections.TIMEFRAME}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
            hasError={sectionErrors.startEndDate}
          />
          <AccordionSection
            idx={3}
            sectionId={Sections.VISIBILITY}
            title="visibility"
            subtitle="leaderboardVisibilitySubtitle"
            details={
              <>
                <VisibilitySection />
                <OLButton
                  className={classes.sectionNextBtn}
                  variant="contained"
                  onClick={() => setActiveSection(Sections.CUSTOM_ATTRIBUTES)}
                >
                  {`${message({ id: "next" })}: ${message({ id: "customAttributes" })}`}
                </OLButton>
              </>
            }
            isEditMode={false}
            isExpanded={activeSection === Sections.VISIBILITY}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
            hasError={sectionErrors.visibility}
          />
          <AccordionSection
            idx={4}
            sectionId={Sections.CUSTOM_ATTRIBUTES}
            title="customAttributes"
            subtitle="customAttributeDescription"
            details={
              <>
                <CustomAttributesSection labels={labels} />
                <OLButton
                  className={classes.sectionNextBtn}
                  variant="contained"
                  onClick={() => setActiveSection(Sections.TRACKED_METRIC)}
                >
                  {`${message({ id: "next" })}: ${message({ id: "trackedMetric" })}`}
                </OLButton>
              </>
            }
            isEditMode={false}
            isExpanded={activeSection === Sections.CUSTOM_ATTRIBUTES}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
            hasError={sectionErrors.attributes}
          />
          <AccordionSection
            idx={4}
            sectionId={Sections.TRACKED_METRIC}
            title="trackedMetric"
            subtitle="trackedMetricDescription"
            details={
              <>
                <TrackedMetricsSection isEditMode={isEditMode} />
                <OLButton
                  className={classes.sectionNextBtn}
                  variant="contained"
                  onClick={() => setActiveSection(Sections.STATUS)}
                >
                  {`${message({ id: "next" })}: ${message({ id: "status" })}`}
                </OLButton>
              </>
            }
            isEditMode={false}
            isExpanded={activeSection === Sections.TRACKED_METRIC}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
            hasError={sectionErrors.attributes}
          />
          <AccordionSection
            idx={5}
            sectionId={Sections.STATUS}
            title="status"
            subtitle="leaderboardStatus"
            details={<Status />}
            isEditMode={false}
            isExpanded={activeSection === Sections.STATUS}
            setExpanded={(panel) => setActiveSection(panel as Sections)}
          />
        </FormSection>
      </Grid>
      <Grid size={{ xs: 4 }}>
        {activeSection === Sections.TITLE && (
          <HelpPanel
            title="leaderboards"
            description={
              <div>
                <Typography>
                  {message({ id: "leaderboardsHelpInfo" })}
                  <img
                    className={classes.img}
                    src={leasdrboardMemberListImg}
                    alt="leaderboard"
                  />
                </Typography>
              </div>
            }
            links={[
              {
                label: message({ id: "documentation" }),
                path: "https://help.openloyalty.io/main-features/leaderboards/creating-leaderboards",
              },
            ]}
          />
        )}
        {activeSection === Sections.TIMEFRAME && (
          <HelpPanel title="timeframe" description="timeframeHelpInfo" />
        )}
        {activeSection === Sections.VISIBILITY && (
          <HelpPanel
            title="leaderboardVisibilityHelp"
            description="leaderboardVisibilityHelpInfo"
          />
        )}
        {activeSection === Sections.TRACKED_METRIC && (
          <HelpPanel
            title="trackedMetrics"
            description="trackedMetricsHelpInfo"
            infoBoxText="trackedMetricsHelpInfoMembers"
          />
        )}
        {activeSection === Sections.STATUS && (
          <HelpPanel
            title="leaderboards"
            description={
              <div>
                <Typography>
                  {message({ id: "leaderboardsStatusHelpInfo" })}
                  <img
                    className={classes.img}
                    src={leasdrboardMemberListImg}
                    alt="leaderboard"
                  />
                </Typography>
              </div>
            }
          />
        )}
      </Grid>
    </Grid>
  );
}
